import * as React from "react";
import { captureMetaTicket, metaTicketValid } from "@/lib/metaLazy";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "@tanstack/react-router";
import { Search, X, Sparkles, Clock, BookOpen } from "lucide-react";
import { type ShopifyProduct } from "@/lib/shopify";
import { searchProductsQueryOptions } from "@/lib/search-products-query";
import { useLocale } from "@/contexts/LocaleContext";
import { smartSearch, suggestTagsForQuery } from "@/lib/smartSearch";
import { useProductTranslations } from "@/hooks/useProductTranslations";
import { notesByHandle } from "@/lib/shopify-localize";
import {
  PRIMARY_MOOD_TAGS,
  SCENT_TAGS,
  getTag,
  normalizeText,
  type TagId,
} from "@/lib/scentTaxonomy";
import { cn } from "@/lib/utils";
import { JOURNAL_POSTS } from "@/data/journal-posts";
import { JOURNAL_LINKS_ENABLED } from "@/lib/feature-flags";

const RECENT_KEY = "search-recent-v1";

export function SearchOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, formatPrice, dir, locale, effectiveMarket, effectiveCountryCode } = useLocale();
  const navigate = useNavigate();
  const [query, setQuery] = React.useState("");
  const [activeTag, setActiveTag] = React.useState<TagId | null>(null);
  const { data: allProducts = [] } = useQuery(searchProductsQueryOptions(effectiveCountryCode, open));
  const [recent, setRecent] = React.useState<string[]>([]);
  const translations = useProductTranslations();
  const notesMap = React.useMemo(() => notesByHandle(translations), [translations]);

  React.useEffect(() => {
    if (!open) return;
    try {
      setRecent(JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"));
    } catch {
      /* ignore */
    }
  }, [open]);

  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }

    setQuery("");
    setActiveTag(null);
  }, [open]);

  const results = React.useMemo<ShopifyProduct[]>(() => {
    if (!allProducts.length) return [];
    if (!query.trim() && !activeTag) return [];
    return smartSearch(allProducts, query, {
      anyTags: activeTag ? [activeTag] : undefined,
      notes: notesMap,
      limit: 8,
    });
  }, [query, activeTag, allProducts, notesMap]);

  const suggestions = React.useMemo(
    () => suggestTagsForQuery(query, locale, SCENT_TAGS, 4),
    [query, locale],
  );

  const journalResults = React.useMemo(() => {
    if (!JOURNAL_LINKS_ENABLED) return [];
    const q = normalizeText(query);
    if (!q || q.length < 2) return [];
    const lang = locale === "ar" ? "ar" : "en";
    return JOURNAL_POSTS.filter((p) => {
      const c = p[lang];
      return (
        normalizeText(c.title).includes(q) ||
        normalizeText(c.excerpt).includes(q) ||
        normalizeText(p.slug).includes(q)
      );
    }).slice(0, 4);
  }, [query, locale]);

  const submit = (term: string) => {
    if (!term.trim()) return;
    try {
      const next = [term, ...recent.filter((r) => r !== term)].slice(0, 5);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      setRecent(next);
    } catch {
      /* ignore */
    }
    import("@/lib/funnelClient").then(({ trackFunnelEvent }) =>
      trackFunnelEvent("search", "search_query", { term: term.slice(0, 100), resultCount: results.length }),
    );
    import("@/lib/ga4").then(({ trackSearch }) => trackSearch(term, results.length));
    // 📊 Meta: Standard Event `Search` — عند تنفيذ البحث فقط (لا على كلّ حرف)
    const metaSearchTicket = captureMetaTicket(); // sync, at the real event
    void (async () => {
      const { detectSearchIntent } = await import("@/lib/search-intent");
      const intent = detectSearchIntent(term);
      if (metaSearchTicket) {
        try {
          const { trackSearchMeta } = await import("@/lib/metaPixel");
          if (metaTicketValid(metaSearchTicket)) {
            trackSearchMeta({
              searchString: term,
              ...(intent ? { contentCategory: intent } : {}),
              resultCount: results.length,
            });
          }
        } catch {
          /* tracking must not block search logging */
        }
      }
      // 🗂️ تخزين داخليّ للتقارير (/admin/search-insights)
      try {
        const { logSearchQuery } = await import("@/lib/searchQueries.functions");
        const { getSessionId } = await import("@/lib/activityTracker");
        await logSearchQuery({
          data: {
            query: term,
            intent,
            resultCount: results.length,
            market: effectiveMarket,
            locale,
            sessionId: getSessionId(),
          },
        });
      } catch {
        /* التتبّع لا يعطّل البحث */
      }
    })();

  };

  const highlight = (text: string) => {
    const q = normalizeText(query);
    if (!q) return text;
    const idx = normalizeText(text).indexOf(q);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="bg-transparent text-primary">{text.slice(idx, idx + q.length)}</mark>
        {text.slice(idx + q.length)}
      </>
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" dir={dir}>
      <button
        className="absolute inset-0 bg-background/95"
        onClick={onClose}
        aria-label="Close search"
      />
      <div className="relative mx-auto mt-0 max-w-3xl border-b border-border bg-background px-6 pb-8 pt-6 text-foreground animate-fade-up">
        <div className="flex items-center gap-3 border-b border-border/60 pb-3">
          <Search className="h-5 w-5 text-primary" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit(query)}
            placeholder={t("search.placeholder")}
            className="flex-1 bg-transparent py-2 text-base placeholder:text-muted-foreground focus:outline-none"
          />
          <button onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Live tag suggestions */}
        {suggestions.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="t-eyebrow-muted">
              {t("search.suggestions")}:
            </span>
            {suggestions.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  setActiveTag(s.id);
                  setQuery("");
                }}
                className="rounded-full border border-primary/40 px-3 py-1 text-xs text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
              >
                {getTag(s.id)?.emoji} {s.label}
              </button>
            ))}
          </div>
        )}

        {activeTag && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="t-eyebrow-muted">{t("search.active_filter") || "الفلتر:"}</span>
            <button
              onClick={() => { setActiveTag(null); setQuery(""); }}
              className="inline-flex items-center gap-1.5 rounded-full border border-primary bg-primary px-3 py-1 text-xs text-primary-foreground transition-colors hover:opacity-90"
              aria-label="Clear filter"
            >
              <span>{getTag(activeTag)?.emoji}</span>
              <span>{t(`tag.${activeTag}`)}</span>
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        <div className="mt-6 max-h-[60vh] space-y-6 overflow-y-auto">
          {results.length > 0 && (
            <ul className="space-y-2">
              {results.map((p) => (
                <li key={p.node.id}>
                  <button
                    onClick={() => {
                      submit(query);
                      import("@/lib/ga4").then(({ trackSelectItem }) =>
                        trackSelectItem({
                          listId: "search",
                          listName: "Search results",
                          product: {
                            id: p.node.handle,
                            title: p.node.title,
                            price: p.node.priceRange.minVariantPrice.amount,
                            currency: p.node.priceRange.minVariantPrice.currencyCode,
                          },
                        }),
                      );
                      onClose();
                      navigate({ to: "/product/$handle", params: { handle: p.node.handle } });
                    }}
                    className="flex w-full items-center gap-4 rounded-none p-2 text-start transition-colors hover:bg-secondary"
                  >
                    {p.node.images.edges[0]?.node.url ? (
                      <img
                        src={p.node.images.edges[0].node.url}
                        alt={p.node.images.edges[0].node.altText ?? p.node.title}
                        className="h-14 w-14 rounded object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-14 w-14 rounded bg-secondary" />
                    )}
                    <div className="flex-1">
                      <p className="font-serif text-base">{highlight(p.node.title)}</p>
                      <p className="text-xs text-muted-foreground">{p.node.productType}</p>
                    </div>
                    <span className="text-sm text-primary">
                      {formatPrice(
                        p.node.priceRange.minVariantPrice.amount,
                        p.node.priceRange.minVariantPrice.currencyCode,
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {journalResults.length > 0 && (
            <div>
              <p className="mb-3 flex items-center gap-2 t-eyebrow-muted">
                <BookOpen className="h-3 w-3" /> {t("search.journal")}
              </p>
              <ul className="space-y-2">
                {journalResults.map((p) => {
                  const c = p[locale === "ar" ? "ar" : "en"];
                  return (
                    <li key={p.slug}>
                      <Link
                        to="/journal/$slug"
                        params={{ slug: p.slug }}
                        onClick={() => {
                          submit(query);
                          onClose();
                        }}
                        className="flex w-full items-center gap-4 rounded-none p-2 text-start transition-colors hover:bg-secondary"
                      >
                        <img
                          src={p.coverImage}
                          alt={c.title}
                          className="h-14 w-14 rounded object-cover"
                          loading="lazy"
                        />
                        <div className="flex-1">
                          <p className="font-serif text-base">{highlight(c.title)}</p>
                          <p className="line-clamp-1 text-xs text-muted-foreground">{c.excerpt}</p>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {(query || activeTag) && allProducts.length && results.length === 0 && journalResults.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t("search.no_results")}
            </p>
          ) : !query && !activeTag ? (
            <>
              {/* Browse by mood */}
              <div>
                <p className="mb-3 flex items-center gap-2 t-eyebrow-muted">
                  <Sparkles className="h-3 w-3" /> {t("search.browse_by_mood")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {PRIMARY_MOOD_TAGS.map((id) => {
                    const tag = getTag(id)!;
                    const active = activeTag === id;
                    return (
                      <button
                        key={id}
                        onClick={() => setActiveTag(active ? null : id)}
                        className={cn(
                          "rounded-full border px-3 py-1.5 text-xs transition-colors",
                          active
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border text-foreground hover:border-primary hover:text-primary",
                        )}
                      >
                        <span className="me-1">{tag.emoji}</span>
                        {t(`tag.${id}`)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {recent.length > 0 && (
                <div>
                  <p className="mb-3 flex items-center gap-2 t-eyebrow-muted">
                    <Clock className="h-3 w-3" /> {t("search.recent")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {recent.map((r) => (
                      <button
                        key={r}
                        onClick={() => setQuery(r)}
                        className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
